import React, { useState, useEffect, useMemo, useCallback, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import ShareMeta from '../../molecules/ShareMeta';
import { Package, Truck, Clock, ShieldCheck, ChevronDown, ChevronUp, Minus, Plus, Play } from 'lucide-react';

import Button from '../../atoms/Button';
import '../Browse/browse.css';
import '../../atoms/QtyStepper/qtyStepper.css';
import './productDetailView.css';

/* THE PRODUCT PAGE — SP-main's `.pp-*` surface.

   A port of `SP-main/product.html`: element order, accordion set, trust rows and
   spec labels come from that file rather than from the rules that style them.

   Presentation only — variant resolution, stock, pricing, the cart payload and
   image ordering are the app's own and are passed in unchanged, the same split
   the `/all-products` port made.

   The buy panel is brand, name, price, variants, quantity + Add to cart, then
   the shipping rows. The description and care-need tags are not shown there:
   the description lives only in the Description section below.

   Not drawn for want of a field: the gallery's VIDEO slot (the reference reads a
   per-product `p.video`; this app has only the brand's `brand_promo`, which goes
   in the brand section). */

/* The heading is always visible; only the body hides — the whole point of this
   over a tab (see the note in the CSS). The body is a region labelled by its own
   header, so a screen reader can reach it by landmark. */
function Section({ id, title, children, open, onToggle }) {
  const bodyId = `${id}-body`;
  const headId = `${id}-head`;
  return (
    <div className={`pp-acc${open ? ' open' : ''}`}>
      <button
        type="button"
        id={headId}
        className="pp-acch"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        {title}
        <span className="pp-accv"><ChevronDown aria-hidden="true" /></span>
      </button>
      <div id={bodyId} role="region" aria-labelledby={headId} className="pp-accb">
        {children}
      </div>
    </div>
  );
}

Section.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
};

/* The stepper. Typing is allowed, but the value is clamped on commit rather than
   on every keystroke, so an empty field while typing is not rewritten to 1. */
function QtyStepper({ value, onChange, max, label }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = (raw) => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1) { onChange(1); setDraft('1'); return; }
    const clamped = max ? Math.min(n, max) : n;
    onChange(clamped);
    setDraft(String(clamped));
  };

  return (
    <div className="sf-qty">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, value - 1))}
        disabled={value <= 1}
        aria-label="Decrease quantity"
      >
        <Minus size={14} aria-hidden="true" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        aria-label={label}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(e.currentTarget.value); } }}
      />
      <button
        type="button"
        onClick={() => onChange(max ? Math.min(max, value + 1) : value + 1)}
        disabled={!!max && value >= max}
        aria-label="Increase quantity"
      >
        <Plus size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

QtyStepper.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.number,
  label: PropTypes.string,
};

QtyStepper.defaultProps = { label: 'Quantity' };

export default function ProductDetailView({
  product,
  description,
  specs,
  related,
  crumb,
  price,
  options,
  brand,
  shipping,
  outOfStock,
  outOfStockLabel,
  quantity,
  onQuantityChange,
  onAddToCart,
  adding,
  fallbackImage,
  variantImage,
}) {
  const uid = useId();

  /* The main photo first, then the rest — the app's own ordering, kept. */
  const images = useMemo(() => {
    const list = product?.product_images?.length ? product.product_images : [{ src: fallbackImage }];
    return [...list].sort((a, b) => (b.is_main === '1') - (a.is_main === '1'));
  }, [product, fallbackImage]);

  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [product?.id]);

  /* The desktop thumb column scrolls with its scrollbar hidden, so arrows and a
     fade mark each end that still has images past it. */
  const thumbsRef = useRef(null);
  const [thumbEdge, setThumbEdge] = useState({ up: false, down: false });
  const readThumbEdge = useCallback(() => {
    const el = thumbsRef.current;
    if (!el) return;
    const up = el.scrollTop > 1;
    const down = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setThumbEdge((cur) => (cur.up === up && cur.down === down ? cur : { up, down }));
  }, []);
  useEffect(() => {
    const el = thumbsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(readThumbEdge);
    ro.observe(el);
    return () => ro.disconnect();
  }, [readThumbEdge, images.length]);
  const scrollThumbs = (dir) => {
    const el = thumbsRef.current;
    const step = el?.firstElementChild?.getBoundingClientRect().height || 68;
    el?.scrollBy({ top: dir * (step + 10), behavior: 'smooth' });
  };

  /* Jump the gallery to the selected variant's photo (BT-179). */
  useEffect(() => {
    const idx = variantImage ? images.findIndex((img) => img.src === variantImage) : -1;
    const next = idx !== -1 ? idx : 0;
    setActive(next);
    const box = thumbsRef.current;
    const thumb = box?.children?.[next];
    if (box && thumb) {
      box.scrollTo({
        top: thumb.offsetTop - (box.clientHeight - thumb.offsetHeight) / 2,
        left: thumb.offsetLeft - (box.clientWidth - thumb.offsetWidth) / 2,
        behavior: 'smooth',
      });
    }
  }, [variantImage, images]);

  const [open, setOpen] = useState('desc');
  const toggle = useCallback((id) => setOpen((cur) => (cur === id ? null : id)), []);

  const [playing, setPlaying] = useState(false);
  useEffect(() => { setPlaying(false); }, [brand?.videoUrl]);

  const onImgError = (e) => { e.target.src = fallbackImage; };

  const hero = images[active] || images[0];
  const title = product?.title || '';

  return (
    <>
    <div className="br-wrap pp-wrap">
      <ShareMeta title={title} image={images[0]?.src || hero?.src} />

      {crumb && <nav className="br-crumb" aria-label="Breadcrumb">{crumb}</nav>}

      <div className="pp-top">
        {/* ---- the gallery ---- */}
        <div className="pp-gal">
          {images.length > 1 && (
            <div className={`pp-thumbsw${thumbEdge.up ? ' can-up' : ''}${thumbEdge.down ? ' can-down' : ''}`}>
              <div className="pp-thumbs" ref={thumbsRef} onScroll={readThumbEdge}>
                {images.map((img, i) => (
                  <button
                    type="button"
                    key={img.id || img.src || i}
                    className={`pp-thumb${i === active ? ' on' : ''}`}
                    aria-label={`Show image ${i + 1} of ${images.length}`}
                    aria-pressed={i === active}
                    onClick={() => setActive(i)}
                  >
                    <img src={img.src || fallbackImage} alt="" loading="lazy" onError={onImgError} />
                  </button>
                ))}
              </div>
              {/* Hidden from the tab order: every thumb is already a button, so
                  keyboard users scroll the column by tabbing through it. */}
              <button type="button" className="pp-tnav up" tabIndex={-1} aria-hidden="true" onClick={() => scrollThumbs(-1)}>
                <ChevronUp aria-hidden="true" />
              </button>
              <button type="button" className="pp-tnav down" tabIndex={-1} aria-hidden="true" onClick={() => scrollThumbs(1)}>
                <ChevronDown aria-hidden="true" />
              </button>
            </div>
          )}

          <div className={`pp-hero${hero?.src ? '' : ' is-ph'}`}>
            {hero?.src
              ? <img src={hero.src} alt={title} onError={onImgError} />
              : <Package aria-hidden="true" />}
          </div>
        </div>

        {/* ---- the buy panel ---- */}
        <div className="pp-buy">
          {brand?.name && (
            brand.href
              ? (
                <Link className="pp-brand" to={brand.href}>
                  <span className="pp-bmark">
                    {brand.logo
                      ? <img src={brand.logo} alt="" onError={onImgError} />
                      : <Package aria-hidden="true" />}
                  </span>
                  {brand.name}
                </Link>
              )
              : (
                <span className="pp-brand">
                  <span className="pp-bmark">
                    {brand.logo
                      ? <img src={brand.logo} alt="" onError={onImgError} />
                      : <Package aria-hidden="true" />}
                  </span>
                  {brand.name}
                </span>
              )
          )}

          <h1 className="pp-name">{title}</h1>

          <div className="pp-price">{price}</div>

          {/* One row per option axis the product declares. An axis whose only
              value is Shopify's "Default Title" is not a choice and is not
              drawn. */}
          {options.map((opt) => (
            <div className="pp-opt" key={opt.key}>
              <span className="pp-optl" id={`${uid}-${opt.key}`}>{opt.name}</span>
              <div className="pp-optvals" role="group" aria-labelledby={`${uid}-${opt.key}`}>
                {opt.values.map((v) => (
                  <button
                    type="button"
                    key={v.value}
                    className={`pp-optv${v.selected ? ' on' : ''}`}
                    disabled={v.disabled}
                    aria-pressed={v.selected}
                    onClick={() => opt.onSelect(v.value)}
                  >
                    {v.value}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="pp-addrow">
            <QtyStepper value={quantity} onChange={onQuantityChange} label={`Quantity of ${title}`} />
            {/* ⛔ No icon. The reference is a plain centred label, and `.pp-add`
                is `flex:1;justify-content:center`, so a leading glyph pushes the
                word off centre. */}
            <Button
              className="pp-add"
              onClick={onAddToCart}
              disabled={outOfStock || adding}
            >
              Add to cart
            </Button>
          </div>

          {outOfStock && <p className="pp-oos" role="status">{outOfStockLabel}</p>}

          {/* ---- the trust rows ----
              Four rows in the reference's order, each with its own glyph:
              shipping cost (truck) · dispatch time (clock) · returns (shield) ·
              "Ships directly from {brand}" (box).

              ⛔ Cost and dispatch are two facts on two lines: joined with a " · "
              they answer two different questions in one unscannable sentence.

              The last row is why the cart makes sense later — each brand ships
              its own parcel, and saying so here is what stops the cart's separate
              shipping lines reading as a mistake. */}
          <ul className="pp-trust">
            {shipping?.cost !== undefined && shipping?.cost !== null && (
              <li>
                <Truck aria-hidden="true" />
                <span>
                  {Number(shipping.cost) === 0
                    ? 'Free shipping'
                    : <>Flat <strong>${shipping.cost}</strong> shipping per order</>}
                </span>
              </li>
            )}
            {shipping?.timeLabel && (
              <li>
                <Clock aria-hidden="true" />
                <span>{shipping.timeLabel}</span>
              </li>
            )}
            {brand?.returns && (
              <li>
                <ShieldCheck aria-hidden="true" />
                <span>{brand.returns}</span>
              </li>
            )}
            <li>
              <Package aria-hidden="true" />
              <span>Ships directly from {brand?.name || 'the brand'}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* ---- the reading matter ----
          ⛔ Not tabs: across 166 measured sites 27% of shoppers never open tabbed
          content, so they miss terms, returns and the brand. This app's desktop
          tab bar also left three sections unreachable above 768px.

          ⛔ Nothing needed in order to buy may be collapsed — price, Add to cart,
          shipping terms, dispatch time and what it is good for stay open in the
          buy panel. Collapsing is for the description, specs and brand.

          Three sections with these exact titles, from product.html's
          `sections()`: Shipping and Returns are rows inside Product details, not
          sections of their own. The first starts open. */}
      <div className="pp-accs">
        {description && (
          <Section id={`${uid}-desc`} title="Description" open={open === 'desc'} onToggle={() => toggle('desc')}>
            <div className="pp-body" dangerouslySetInnerHTML={{ __html: description }} />
          </Section>
        )}

        {/* Only rows with a real field behind them. Brand and Category are links;
            Shipping stacks its lines with <br>, as `Shop.shipLines()` does. */}
        {specs?.length > 0 && (
          <Section id={`${uid}-specs`} title="Product details" open={open === 'specs'} onToggle={() => toggle('specs')}>
            <dl className="pp-specs">
              {specs.map((row) => (
                <div key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </Section>
        )}

        {/* "About X" — matches the brand page's heading. */}
        {(brand?.story || brand?.values?.length > 0 || brand?.videoUrl || brand?.community) && (
          <Section
            id={`${uid}-brand`}
            title={brand?.name ? `About ${brand.name}` : 'About the brand'}
            open={open === 'brand'}
            onToggle={() => toggle('brand')}
          >
            {brand.story && <p className="pp-body">{brand.story}</p>}
            {brand.community && <p className="pp-body">{brand.community}</p>}

            {brand.values?.length > 0 && (
              <div className="pp-vals">
                {brand.values.map((v) => <span className="pp-val" key={v}>{v}</span>)}
              </div>
            )}

            {brand.videoUrl && (
              <div className={`pp-bvid${playing ? ' playing' : ''}`}>
                {playing ? (
                  <iframe
                    src={brand.embedUrl}
                    title={`${brand.name || 'Brand'} video`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                ) : (
                  <>
                    {brand.logo && <img src={brand.logo} alt="" onError={onImgError} />}
                    <button
                      type="button"
                      className="pp-vplay"
                      onClick={() => setPlaying(true)}
                      aria-label={`Play ${brand.name || 'brand'} video`}
                    >
                      <Play fill="currentColor" aria-hidden="true" />
                    </button>
                    <span className="pp-vnow">
                      <span className="pp-vt">{brand.name || 'This brand'}&rsquo;s video</span>
                      <span className="pp-vd">Plays here</span>
                    </span>
                  </>
                )}
              </div>
            )}

            {brand.href && (
              <Link className="sf-seeall" to={brand.href}>
                All products from {brand.name || 'this brand'} &rarr;
              </Link>
            )}
          </Section>
        )}
      </div>

    </div>

      {/* The rail sits outside `.br-wrap`, as in the reference: `related()` is
          appended after the wrapper closes, so the section is full-bleed with its
          own 40px gutter. Inside, it would be guttered twice. */}
      {related}

      {/* ---- the pinned bar, phones only (≤767px in CSS) ----
          On a phone the buy panel scrolls away behind the description and the
          rail. This is the SAME control, not a second one: same handler, same
          quantity. */}
      <div className="pp-sticky">
        <div className="pp-stickyin">
          <div>
            <div className="pp-sname">{title}</div>
            <div className="pp-sprice">{price}</div>
          </div>
          <Button onClick={onAddToCart} disabled={outOfStock || adding}>
            Add to cart
          </Button>
        </div>
      </div>
    </>
  );
}

ProductDetailView.propTypes = {
  product: PropTypes.object,
  /** `body_html`, already decoded by the page — see `decodeDesc` there. */
  description: PropTypes.string,
  /** "What it's good for" chips. Empty unless the product carries tags. */
  /** Product details rows: `[{ label, value }]`, value may be a node. */
  specs: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.node,
  })),
  /** The "You might also like" section, rendered full-bleed below the page. */
  related: PropTypes.node,
  crumb: PropTypes.node,
  price: PropTypes.node,
  /** One entry per option axis: { key, name, values:[{value,selected,disabled}], onSelect }. */
  options: PropTypes.array,
  brand: PropTypes.object,
  shipping: PropTypes.object,
  outOfStock: PropTypes.bool,
  outOfStockLabel: PropTypes.string,
  quantity: PropTypes.number,
  onQuantityChange: PropTypes.func,
  onAddToCart: PropTypes.func,
  adding: PropTypes.bool,
  fallbackImage: PropTypes.string,
  variantImage: PropTypes.string,
};

ProductDetailView.defaultProps = {
  options: [],
  specs: [],
  quantity: 1,
  outOfStockLabel: 'Out of stock',
};
